import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { DownloadSimple } from '@phosphor-icons/react';
import { downloadExport } from '@/lib/exporter';

export const SettlementPage = () => {
  const { t } = useLanguage();
  const [settlements, setSettlements] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [products, setProducts] = useState([]);
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth());
  const [selectedYear, setSelectedYear] = useState(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear());
  const [expandedId, setExpandedId] = useState(null);
  const [invoiceSST, setInvoiceSST] = useState({});

  useEffect(() => { fetchData(); }, [selectedMonth, selectedYear]);

  const fetchData = async () => {
    try {
      const [s, ph, pr] = await Promise.all([
        api.get(`/settlements?month=${selectedMonth}&year=${selectedYear}`),
        api.get('/pharmacies'),
        api.get('/products'),
      ]);
      setSettlements(s.data);
      setPharmacies(ph.data);
      setProducts(pr.data);
    } catch (e) { console.error(e); }
  };

  const getProductName = (id) => products.find(p => p.id === id)?.name || id?.slice(-6);
  const getPharmacyName = (id) => pharmacies.find(p => p.id === id)?.name || 'Unknown';

  const generateAll = async () => {
    try {
      // Item 1 helper: also refresh every partner statement (pending only) for the same period
      // so an admin's single "Generate All" click keeps statements in sync with settlements.
      const partnersRes = await api.get('/partners').catch(() => ({ data: [] }));
      for (const p of pharmacies) {
        try {
          await api.post(`/settlements/generate/${p.id}/${selectedMonth}/${selectedYear}`);
        } catch (e) { /* skip if confirmed */ }
      }
      for (const pt of (partnersRes.data || [])) {
        try {
          await api.post(`/partner-statements/generate/${pt.id}/${selectedMonth}/${selectedYear}`);
        } catch (e) { /* skip if paid */ }
      }
      toast.success('Settlements and partner statements refreshed');
      fetchData();
    } catch (e) {
      toast.error('Failed to generate');
    }
  };

  const handleConfirm = async (settlementId, s) => {
    // Item 5: warn admin when confirming a settlement for the current unfinished month
    const now = new Date();
    const periodEnd = new Date(s.year, s.month, 1); // first day of the NEXT month
    const notEnded = now < periodEnd;
    let ackQuery = '';
    if (notEnded) {
      const ok = window.confirm(
        `Period ${String(s.month).padStart(2, '0')}/${s.year} has not ended yet.\n\n`
        + `Any sales reported after confirmation will be carried into the NEXT period as prior-period adjustments.\n\n`
        + `Confirm anyway?`
      );
      if (!ok) return;
      ackQuery = '?ack_current_period=true';
    }
    try {
      await api.post(`/settlements/${settlementId}/confirm${ackQuery}`);
      toast.success('Settlement confirmed');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to confirm');
    }
  };

  const handleGenerateInvoice = async (settlementId, sstEnabled = false) => {
    try {
      await api.post('/invoices', { settlement_id: settlementId, sst_enabled: sstEnabled });
      toast.success('Invoice generated');
      fetchData();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail
        || (typeof e.response?.data === 'string' ? e.response.data : null)
        || e.message
        || 'Unknown error';
      toast.error(`Invoice failed${status ? ` (${status})` : ''}: ${detail}`, { duration: 8000 });
      console.error('Invoice generation error:', e.response || e);
    }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>{t('nav.settlement')}</h1>
        <div className="flex gap-2">
          <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
            <SelectTrigger className="w-32" data-testid="month-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Array.from({length: 12}, (_, i) => i + 1).map(m => (
                <SelectItem key={m} value={String(m)}>{new Date(2000, m - 1).toLocaleString('en', {month: 'long'})}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-24" data-testid="year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="generate-settlements-btn" onClick={generateAll} style={{ backgroundColor: '#1E3A8A' }}>
            Generate All
          </Button>
          <Button data-testid="export-settlements-btn" variant="outline" onClick={() => downloadExport(`/exports/settlements?month=${selectedMonth}&year=${selectedYear}`, `Settlements_${String(selectedMonth).padStart(2,'0')}-${selectedYear}.xlsx`)}>
            <DownloadSimple size={14} className="mr-2" /> Export
          </Button>
        </div>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left p-3">Pharmacy</th>
              <th className="text-right p-3">Sales</th>
              <th className="text-right p-3">Commission</th>
              <th className="text-right p-3">Amount Due</th>
              <th className="text-center p-3">Reconciliation</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {settlements.map((s) => {
              const hasMismatch = s.reconciliation?.some(r => r.difference !== 0);
              return (
                <React.Fragment key={s.id}>
                  <tr className="border-t border-slate-200 cursor-pointer hover:bg-slate-50" onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}>
                    <td className="p-3 font-medium">{getPharmacyName(s.pharmacy_id)}</td>
                    <td className="p-3 text-right">RM {s.total_sales?.toFixed(2)}</td>
                    <td className="p-3 text-right">RM {s.pharmacy_commission?.toFixed(2)}</td>
                    <td className="p-3 text-right font-bold">RM {s.amount_due?.toFixed(2)}</td>
                    <td className="p-3 text-center">
                      {hasMismatch ? (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">✗ Mismatch</span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">✓ Tally</span>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        s.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                        s.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                        'bg-green-100 text-green-700'
                      }`}>{s.status.toUpperCase()}</span>
                    </td>
                    <td className="p-3 space-x-2">
                      {s.status === 'draft' && (
                        <Button data-testid={`confirm-settle-${s.id}`} onClick={(e) => { e.stopPropagation(); handleConfirm(s.id, s); }} size="sm" style={{ backgroundColor: '#1E3A8A' }}>
                          Confirm
                        </Button>
                      )}
                      {s.status === 'confirmed' && (
                        <div className="flex items-center gap-2">
                          <label className="flex items-center gap-1 text-xs">
                            <input type="checkbox" data-testid={`sst-toggle-${s.id}`} checked={!!invoiceSST[s.id]} onChange={e => setInvoiceSST({ ...invoiceSST, [s.id]: e.target.checked })} />
                            SST 8%
                          </label>
                          <Button data-testid={`gen-invoice-${s.id}`} onClick={(e) => { e.stopPropagation(); handleGenerateInvoice(s.id, !!invoiceSST[s.id]); }} size="sm" className="bg-green-600 hover:bg-green-700">
                            Generate Invoice
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {expandedId === s.id && s.reconciliation?.length > 0 && (
                    <tr>
                      <td colSpan="7" className="p-4 bg-slate-50">
                        <div className="text-sm space-y-2">
                          <p className="font-semibold">Reconciliation Details:</p>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-slate-600">
                                <th className="text-left">Product</th>
                                <th className="text-right">Opening</th>
                                <th className="text-right">Received</th>
                                <th className="text-right">Sold</th>
                                <th className="text-right">Expected</th>
                                <th className="text-right">Actual</th>
                                <th className="text-right">Difference</th>
                              </tr>
                            </thead>
                            <tbody>
                              {s.reconciliation.map((r, i) => (
                                <tr key={i}>
                                  <td>{getProductName(r.product_id)}</td>
                                  <td className="text-right">{r.opening_stock}</td>
                                  <td className="text-right">{r.received}</td>
                                  <td className="text-right">{r.sold}</td>
                                  <td className="text-right">{r.expected_closing}</td>
                                  <td className="text-right">{r.actual_closing}</td>
                                  <td className={`text-right font-bold ${r.difference !== 0 ? 'text-red-600' : 'text-green-600'}`}>{r.difference}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {settlements.length === 0 && (
              <tr><td colSpan="7" className="p-8 text-center text-slate-500">No settlements for this period. Click "Generate All" to create.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
};
